import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarClock, FileText, PartyPopper, Contact, ShieldCheck,
  AlertTriangle, Copy, Mail, Download,
} from 'lucide-react';
import {
  ms, isAdminConsentRequiredError, getAdminConsentUrl,
} from '../services/calendarAdapters.js';

const CATEGORIES = [
  { key: 'appointment',  title: 'Appointment',   subtitle: 'Doctor card, dentist, vet', Icon: CalendarClock },
  { key: 'bill',         title: 'Bill',          subtitle: 'Due date reminder',         Icon: FileText },
  { key: 'flyer',        title: 'Event flyer',   subtitle: 'Party, school event',       Icon: PartyPopper },
  { key: 'businesscard', title: 'Business card', subtitle: 'Save to Outlook contacts',  Icon: Contact },
];

export default function HomeScreen() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(() => ms.getAccount());
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [adminRequired, setAdminRequired] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setAccount(ms.getAccount()); }, []);

  const handleConnect = async () => {
    setError(null);
    setAdminRequired(false);
    setConnecting(true);
    try {
      const acc = await ms.signIn();
      setAccount(acc);
    } catch (e) {
      console.error(e);
      if (isAdminConsentRequiredError(e)) {
        setAdminRequired(true);
      } else if (e.errorCode !== 'user_cancelled') {
        setError(e.message || e.errorMessage || 'Sign-in failed. Try again.');
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleCopyAdminLink = async () => {
    const url = getAdminConsentUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link to send to your IT admin:', url);
    }
  };

  const adminEmailBody = encodeURIComponent(
    `Hi,\n\nI'd like to use ScanRemind to add appointments and contacts to my Outlook calendar from photos. It's a Microsoft-verified publisher (Vital Integrators, MPN 3717176) that only requests permission to read and write my own calendar and contacts — nothing else.\n\nPlease approve it for our tenant by clicking this one-time admin consent link:\n\n${getAdminConsentUrl()}\n\nMore about ScanRemind: https://scanremind.com\n\nThanks!`
  );
  const adminMailto = `mailto:?subject=${encodeURIComponent('Please approve ScanRemind for our Microsoft 365 tenant')}&body=${adminEmailBody}`;

  // ─── Admin approval needed ──────────────────────────────────────────────
  if (adminRequired) {
    return (
      <div>
        <h1 className="h1">IT admin approval needed</h1>
        <p className="muted" style={{ marginBottom: 20 }}>
          Your organization's Microsoft 365 settings require an admin to approve
          new apps before users can connect them. Don't worry — they just need
          to click one link.
        </p>

        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
            <div className="tile__icon" style={{ background: '#fef3c7', color: '#92400e' }}>
              <AlertTriangle size={20} />
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>What your admin needs to do</div>
              <div className="muted">
                Click the approval link below (or that you send them). They'll sign into
                Microsoft, see ScanRemind's verified publisher badge (Vital Integrators),
                review what we ask for, and approve. After that, everyone at your org can
                use ScanRemind without further prompts.
              </div>
            </div>
          </div>

          <a className="btn btn--primary btn--block" href={adminMailto} style={{ marginBottom: 10 }}>
            <Mail size={18} /> Email my admin the approval link
          </a>

          <button className="btn btn--ghost btn--block" onClick={handleCopyAdminLink} style={{ marginBottom: 10 }}>
            <Copy size={18} /> {copied ? 'Copied!' : 'Copy approval link to clipboard'}
          </button>

          <button className="btn btn--ghost btn--block" onClick={() => setAdminRequired(false)}>
            ← Back
          </button>
        </div>

        <div className="card" style={{ background: 'var(--accent-soft)', border: 0 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Download size={20} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Don't want to wait?</div>
              <div className="muted">
                You can still use ScanRemind without admin approval — just download .ics or .vcf
                files instead of writing directly to Outlook. Tap a category to start.
              </div>
              <button
                className="btn btn--primary"
                onClick={() => setAdminRequired(false)}
                style={{ marginTop: 12 }}
              >
                Continue without connecting
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Not connected: connect-first prompt ────────────────────────────────
  if (!account) {
    return (
      <div>
        <h1 className="h1">Connect Microsoft 365</h1>
        <p className="muted" style={{ marginBottom: 20 }}>
          One tap to authorize ScanRemind to add events and contacts to your Outlook.
          You'll see a Microsoft sign-in popup — accept once, and you're set forever on this device.
        </p>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
            <div className="tile__icon"><ShieldCheck size={20} /></div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>What we ask for</div>
              <ul className="muted" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Read &amp; write your calendar</li>
                <li>Read &amp; write your contacts</li>
                <li>Sign in once, stay signed in</li>
              </ul>
            </div>
          </div>
          <button
            className="btn btn--primary btn--block"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? <><div className="spinner" /> Opening sign-in…</> : 'Connect Microsoft 365'}
          </button>
        </div>

        {error && (
          <div className="card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>
            {error}
          </div>
        )}

        <div className="card" style={{ background: 'var(--surface-2)', border: 0, marginTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Don't use Microsoft 365?</div>
          <div className="muted" style={{ marginBottom: 12 }}>
            You can still scan photos and download universal .ics / .vcf files that work
            with Apple Calendar, Google Calendar, Yahoo, Outlook desktop — anywhere.
          </div>
          <button className="btn btn--ghost" onClick={() => setAccount({ username: '_skip_' })}>
            Skip — just use file downloads
          </button>
        </div>

        <p className="muted" style={{ fontSize: 12, marginTop: 16 }}>
          ScanRemind never sees your password. Sign-in happens directly with Microsoft.
          Nothing is stored on our servers.
        </p>
      </div>
    );
  }

  // ─── Connected: show tiles ──────────────────────────────────────────────
  return (
    <div>
      <h1 className="h1">What did you snap?</h1>
      <p className="muted" style={{ marginBottom: 20 }}>
        Pick a category — we'll send the photo to AI and pull out the details.
      </p>
      <div className="tile-grid">
        {CATEGORIES.map(({ key, title, subtitle, Icon }) => (
          <button
            key={key}
            className="tile"
            onClick={() => navigate(`/capture/${key}`)}
          >
            <div className="tile__icon"><Icon size={20} /></div>
            <div className="tile__title">{title}</div>
            <div className="tile__subtitle">{subtitle}</div>
          </button>
        ))}
      </div>
      {account.username && account.username !== '_skip_' && (
        <p className="muted" style={{ fontSize: 12, marginTop: 20, textAlign: 'center' }}>
          Signed in as {account.username}
        </p>
      )}
      {account.username === '_skip_' && (
        <p className="muted" style={{ fontSize: 12, marginTop: 20, textAlign: 'center' }}>
          Not connected to Microsoft · scans download as files
        </p>
      )}
    </div>
  );
}
